/**
 * デザイン付きチケットPDF生成 v2
 * 座席情報対応 + A4レイアウト対応
 */

import { getTemplate } from './ticketTemplates';

/**
 * A4用紙に複数チケットを配置してPDF生成
 */
export const generateDesignedTicketPDF_A4Layout = async (qrCodes, ticketName, templateId = 'modern', options = {}) => {
  if (typeof window === 'undefined') {
    throw new Error('This function must be called from PC/Web environment');
  }

  console.log('[PDF Generator A4] Starting...');
  console.log('[PDF Generator A4] QR codes:', qrCodes?.length);

  if (!Array.isArray(qrCodes) || qrCodes.length === 0) {
    throw new Error('PDF化するQRコードがありません');
  }

  try {
    const html2canvasModule = await import('html2canvas');
    const html2canvas = html2canvasModule.default || html2canvasModule;
    const { jsPDF } = await import('jspdf');

    const template = getTemplate(templateId);
    const pdf = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'a4',
      compress: true
    });

    // A4サイズ: 210mm x 297mm
    const margin = 10;
    const ticketWidth = 190;
    const ticketHeight = 90;
    const ticketsPerPage = 2;
    const gap = 10;

    let currentPage = 0;
    let ticketOnPage = 0;

    for (let i = 0; i < qrCodes.length; i++) {
      const qr = qrCodes[i];
      
      if (!qr?.imageData || !qr?.code) {
        console.warn(`[PDF Generator A4] Skipping QR ${i + 1}: missing data`);
        continue;
      }

      if (ticketOnPage === 0 && currentPage > 0) {
        pdf.addPage();
      }

      // 座席情報を個別に設定可能
      const seatInfo = options.seats && options.seats[i] ? options.seats[i] : options.seatInfo || '';

      const ticketData = {
        eventName: ticketName || 'イベント',
        eventDate: options.eventDate || '',
        ticketNumber: qr.code,
        holderName: options.holderName || '',
        seatInfo: seatInfo,
        freeText: qr.freeText || '',
        isHtmlMode: options.isHtmlMode || false,
        qrCodeDataUrl: qr.imageData
      };

      const htmlContent = template(ticketData, 'standard');

      const container = document.createElement('div');
      container.style.position = 'absolute';
      container.style.left = '-9999px';
      container.style.top = '-9999px';
      container.innerHTML = htmlContent;
      document.body.appendChild(container);

      try {
        const canvas = await html2canvas(container.firstElementChild, {
          scale: 2,
          useCORS: true,
          logging: false,
          backgroundColor: null
        });

        const imgData = canvas.toDataURL('image/png');
        const yPos = margin + ticketOnPage * (ticketHeight + gap);
        
        pdf.addImage(imgData, 'PNG', margin, yPos, ticketWidth, ticketHeight, undefined, 'FAST');

        ticketOnPage++;
        if (ticketOnPage >= ticketsPerPage) {
          ticketOnPage = 0;
          currentPage++;
        }

        if (options.onProgress) {
          options.onProgress(i + 1, qrCodes.length);
        }
      } finally {
        document.body.removeChild(container);
      }
    }

    const safeTicketName = String(ticketName || 'ticket').replace(/[\\/:*?"<>|]/g, '_');
    const fileName = `ticket_${safeTicketName}_${templateId}_a4_${new Date().toISOString().split('T')[0]}.pdf`;
    pdf.save(fileName);

  } catch (error) {
    console.error('[PDF Generator A4] Failed:', error);
    throw error;
  }
};

/**
 * 1枚ずつPDF生成（標準サイズ）
 */
export const generateDesignedTicketPDF_Single = async (qrCodes, ticketName, templateId = 'modern', options = {}) => {
  if (typeof window === 'undefined') {
    throw new Error('This function must be called from PC/Web environment');
  }

  console.log('[PDF Generator Single] Starting...');

  if (!Array.isArray(qrCodes) || qrCodes.length === 0) {
    throw new Error('PDF化するQRコードがありません');
  }

  try {
    const html2canvasModule = await import('html2canvas');
    const html2canvas = html2canvasModule.default || html2canvasModule;
    const { jsPDF } = await import('jspdf');

    const template = getTemplate(templateId);
    const pdf = new jsPDF({
      orientation: 'landscape',
      unit: 'mm',
      format: [210, 99], // 標準チケットサイズ
      compress: true
    });

    let isFirstPage = true;

    for (let i = 0; i < qrCodes.length; i++) {
      const qr = qrCodes[i];
      
      if (!qr?.imageData || !qr?.code) {
        console.warn(`[PDF Generator Single] Skipping QR ${i + 1}: missing data`);
        continue;
      }

      if (!isFirstPage) {
        pdf.addPage();
      }
      isFirstPage = false;

      // 座席情報を個別に設定可能
      const seatInfo = options.seats && options.seats[i] ? options.seats[i] : options.seatInfo || '';

      const ticketData = {
        eventName: ticketName || 'イベント',
        eventDate: options.eventDate || '',
        ticketNumber: qr.code,
        holderName: options.holderName || '',
        seatInfo: seatInfo,
        freeText: qr.freeText || '',
        isHtmlMode: options.isHtmlMode || false,
        qrCodeDataUrl: qr.imageData
      };

      const htmlContent = template(ticketData, 'standard');

      const container = document.createElement('div');
      container.style.position = 'absolute';
      container.style.left = '-9999px';
      container.style.top = '-9999px';
      container.innerHTML = htmlContent;
      document.body.appendChild(container);

      try {
        const canvas = await html2canvas(container.firstElementChild, {
          scale: 2,
          useCORS: true,
          logging: false,
          backgroundColor: null
        });

        const imgData = canvas.toDataURL('image/png');
        const pdfWidth = pdf.internal.pageSize.getWidth();
        const pdfHeight = pdf.internal.pageSize.getHeight();
        
        pdf.addImage(imgData, 'PNG', 0, 0, pdfWidth, pdfHeight, undefined, 'FAST');

        if (options.onProgress) {
          options.onProgress(i + 1, qrCodes.length);
        }
      } finally {
        document.body.removeChild(container);
      }
    }

    const safeTicketName = String(ticketName || 'ticket').replace(/[\\/:*?"<>|]/g, '_');
    const fileName = `ticket_${safeTicketName}_${templateId}_single_${new Date().toISOString().split('T')[0]}.pdf`;
    pdf.save(fileName);

  } catch (error) {
    console.error('[PDF Generator Single] Failed:', error);
    throw error;
  }
};


/**
 * 携帯用縦レイアウトPDF生成（スマホ向け）
 */
export const generateDesignedTicketPDF_Mobile = async (qrCodes, ticketName, templateId = 'modern', options = {}) => {
  if (typeof window === 'undefined') {
    throw new Error('This function must be called from PC/Web environment');
  }

  console.log('[PDF Generator Mobile] Starting...');

  if (!Array.isArray(qrCodes) || qrCodes.length === 0) {
    throw new Error('PDF化するQRコードがありません');
  }

  try {
    const html2canvasModule = await import('html2canvas');
    const html2canvas = html2canvasModule.default || html2canvasModule;
    const { jsPDF } = await import('jspdf');

    const template = getTemplate(templateId);
    const pdf = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: [105, 148], // スマホ画面サイズ（A6相当）
      compress: true
    });

    let isFirstPage = true;

    for (let i = 0; i < qrCodes.length; i++) {
      const qr = qrCodes[i];
      
      if (!qr?.imageData || !qr?.code) {
        console.warn(`[PDF Generator Mobile] Skipping QR ${i + 1}: missing data`);
        continue;
      }

      if (!isFirstPage) {
        pdf.addPage();
      }
      isFirstPage = false;

      // 座席情報を個別に設定可能
      const seatInfo = options.seats && options.seats[i] ? options.seats[i] : options.seatInfo || '';

      const ticketData = {
        eventName: ticketName || 'イベント',
        eventDate: options.eventDate || '',
        ticketNumber: qr.code,
        holderName: options.holderName || '',
        seatInfo: seatInfo,
        freeText: qr.freeText || '',
        isHtmlMode: options.isHtmlMode || false,
        qrCodeDataUrl: qr.imageData
      };

      const htmlContent = template(ticketData, 'mobile');

      const container = document.createElement('div');
      container.style.position = 'absolute';
      container.style.left = '-9999px';
      container.style.top = '-9999px';
      container.innerHTML = htmlContent;
      document.body.appendChild(container);

      try {
        const canvas = await html2canvas(container.firstElementChild, {
          scale: 2,
          useCORS: true,
          logging: false,
          backgroundColor: null
        });

        const imgData = canvas.toDataURL('image/png');
        const pdfWidth = pdf.internal.pageSize.getWidth();
        const pdfHeight = pdf.internal.pageSize.getHeight();
        
        pdf.addImage(imgData, 'PNG', 0, 0, pdfWidth, pdfHeight, undefined, 'FAST');

        if (options.onProgress) {
          options.onProgress(i + 1, qrCodes.length);
        }
      } finally {
        document.body.removeChild(container);
      }
    }

    const safeTicketName = String(ticketName || 'ticket').replace(/[\\/:*?"<>|]/g, '_');
    const fileName = `ticket_${safeTicketName}_${templateId}_mobile_${new Date().toISOString().split('T')[0]}.pdf`;
    pdf.save(fileName);

  } catch (error) {
    console.error('[PDF Generator Mobile] Failed:', error);
    throw error;
  }
};